"use client";

import { PageHeader, Panel } from "@/components/ui";
import { useI18n } from "@/lib/i18n/provider";

/**
 * About Terra Listens and the Terms of Service, rendered from
 * product/copy.json#/legal.*.
 *
 * This replaces src/lib/content/legal.ts, which held ~150 lines of legal prose
 * pasted into a TypeScript file — the "Current Web has hardcoded legacy legal
 * copy" finding in web-ios-parity.json. The document text now comes from the
 * same keys iOS renders, so the two cannot drift.
 *
 * The PRIVACY POLICY is deliberately absent. SPEC-Web.md: "The public privacy
 * page must not ship until product/privacy-policy.json is approved; unresolved
 * draft tokens are forbidden in production." /privacy is a route in
 * $webSurface and lands with that approval, not before.
 *
 * The outline below is keys, never prose — the structure is ours, the words are
 * the contract's.
 */
const ABOUT_SECTIONS: Array<{ headingKey?: string; bodyKeys: string[] }> = [
  { bodyKeys: ["legal.about.tagline", "legal.about.mission.body"] },
  {
    headingKey: "legal.about.ourStory.heading",
    bodyKeys: ["legal.about.ourStory.p1", "legal.about.ourStory.p2"],
  },
  {
    headingKey: "legal.about.terraAndCtt.heading",
    bodyKeys: ["legal.about.terraAndCtt.p1", "legal.about.terraAndCtt.p2"],
  },
  {
    headingKey: "legal.about.contact.heading",
    bodyKeys: [
      "legal.about.contact.support",
      "legal.about.contact.press",
      "legal.about.contact.research",
    ],
  },
];

const TEAM = [
  { nameKey: "legal.about.team.mikeLanzone.name", bioKey: "legal.about.team.mikeLanzone.bio" },
  { nameKey: "legal.about.team.scottWhittle.name", bioKey: "legal.about.team.scottWhittle.bio" },
  { nameKey: "legal.about.team.caseyHalverson.name", bioKey: "legal.about.team.caseyHalverson.bio" },
];

/**
 * legal.terms.s1 .. s12, each a title + body pair.
 *
 * Spelled out rather than built with a template literal on purpose:
 * scripts/build-contract.mjs discovers keys by scanning for string literals, so
 * a computed key would silently fall out of the generated catalogue and render
 * as a raw key at the user. If a key has to be constructed, it has to be
 * written down.
 */
const TERMS_SECTIONS = [
  { titleKey: "legal.terms.s1.title", bodyKey: "legal.terms.s1.body" },
  { titleKey: "legal.terms.s2.title", bodyKey: "legal.terms.s2.body" },
  { titleKey: "legal.terms.s3.title", bodyKey: "legal.terms.s3.body" },
  { titleKey: "legal.terms.s4.title", bodyKey: "legal.terms.s4.body" },
  { titleKey: "legal.terms.s5.title", bodyKey: "legal.terms.s5.body" },
  { titleKey: "legal.terms.s6.title", bodyKey: "legal.terms.s6.body" },
  { titleKey: "legal.terms.s7.title", bodyKey: "legal.terms.s7.body" },
  { titleKey: "legal.terms.s8.title", bodyKey: "legal.terms.s8.body" },
  { titleKey: "legal.terms.s9.title", bodyKey: "legal.terms.s9.body" },
  { titleKey: "legal.terms.s10.title", bodyKey: "legal.terms.s10.body" },
  { titleKey: "legal.terms.s11.title", bodyKey: "legal.terms.s11.body" },
  { titleKey: "legal.terms.s12.title", bodyKey: "legal.terms.s12.body" },
];

export function LegalPage() {
  const { t } = useI18n();

  return (
    <div className="tk-stack tk-stack-lg">
      <PageHeader titleKey="web.legal.title" back={{ href: "/settings" }} />

      <div className="tk-columns">
        <Panel titleKey="legal.about.title">
          <div className="tk-stack">
            {ABOUT_SECTIONS.map((section, index) => (
              <section key={section.headingKey ?? `about-${index}`} className="tk-stack tk-stack-sm">
                {section.headingKey ? (
                  <h3 className="tk-headline">{t(section.headingKey)}</h3>
                ) : null}
                {section.bodyKeys.map((key) => (
                  <p key={key} className="tk-body tk-secondary">
                    {t(key)}
                  </p>
                ))}
              </section>
            ))}

            <blockquote className="tk-sunken tk-stack tk-stack-xs">
              <p className="tk-body">{t("legal.about.quote.text")}</p>
              <p className="tk-caption tk-secondary">{t("legal.about.quote.attribution")}</p>
            </blockquote>

            <section className="tk-stack tk-stack-sm">
              <h3 className="tk-headline">{t("legal.about.ourTeam.heading")}</h3>
              {TEAM.map((member) => (
                <div key={member.nameKey} className="tk-stack tk-stack-xs">
                  <p className="tk-body tk-strong">{t(member.nameKey)}</p>
                  <p className="tk-body tk-secondary">{t(member.bioKey)}</p>
                </div>
              ))}
            </section>
          </div>
        </Panel>

        <Panel titleKey="legal.terms.title">
          <div className="tk-stack">
            <p className="tk-caption tk-muted">{t("legal.terms.effectiveDate")}</p>
            {TERMS_SECTIONS.map((section) => (
              <section key={section.titleKey} className="tk-stack tk-stack-xs">
                <h3 className="tk-headline">{t(section.titleKey)}</h3>
                <p className="tk-body tk-secondary">{t(section.bodyKey)}</p>
              </section>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
